import { useCallback } from "react"

import DecimalMath from "../../../utils/DecimalMath"
import { parseNumber } from "../../../utils/NumberUtils"

const spin = (type: "up" | "down", currentValue: string, min: number, max: number, step: number) => {
  const valueAsNumber = parseNumber(currentValue, 0)

  if (type === "up") {
    if (valueAsNumber < min) {
      return min
    }
    const nextNumber = DecimalMath.add(valueAsNumber, step)
    return Math.min(nextNumber, max)
  }
  if (valueAsNumber > max) {
    return max
  }
  const nextNumber = DecimalMath.sub(valueAsNumber, step)
  return Math.max(nextNumber, min)
}

const useSpin = (
  options: {
    min: number
    max: number
    step: number
  },
  isComposing: boolean,
  readOnly: boolean,
  updateValue: (valueAsNumber: (prev: string) => number) => void
) => {
  const { min, max, step } = options

  const onUp = useCallback(() => {
    updateValue((prev) => spin("up", prev, min, max, step))
  }, [max, min, step, updateValue])

  const onDown = useCallback(() => {
    updateValue((prev) => spin("down", prev, min, max, step))
  }, [max, min, step, updateValue])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isComposing || readOnly) return

      switch (e.key) {
        case "ArrowUp":
          onUp()
          e.preventDefault()
          break
        case "ArrowDown":
          onDown()
          e.preventDefault()
          break
      }
    },
    [isComposing, onDown, onUp, readOnly]
  )

  return {
    onUp,
    onDown,
    onKeyDown,
  }
}

export default useSpin
